import React, { useEffect, useState } from 'react';
import { FiCheck, FiChevronDown } from 'react-icons/fi';

/**
 * A choice card: when a carrier tool has several options for the person
 * (which policy), it returns one signed link
 * (`https://<carrier server>/choices/<id>?e=…&s=…`, see choiceReply in
 * automation/mcp-server/server.mjs). The chat shows it here, in the chat's
 * own table style:
 *  - a round check on the left of each row — tick one or several, then send
 *    them with the button under the table;
 *  - a drop-down on the right of each row with the policy's core details: what
 *    the search showed at once, then the policy's own details read from the
 *    carrier (loaded the first time the row is opened).
 * Sending says the pick as if the person had typed it. Works the same for
 * every carrier.
 */
export const CHOICE_LINK = /^https?:\/\/[^/\s]+\/choices\/[a-f0-9]+\?e=\d+&s=[a-f0-9]+$/;

const HEADERS = { 'ngrok-skip-browser-warning': '1' };

interface Detail { label: string; value: string }
interface ChoiceOption { label: string; value?: string; say: string; cells?: string[]; details?: Detail[] }
interface ChoiceSet {
  title: string;
  columns: string[] | null;
  options: ChoiceOption[];
  multi: { say: string; button: string } | null;
  carrier: string | null;
  detailsAvailable: boolean;
}
type CarrierDetails = { state: 'loading' } | { state: 'done'; details: Detail[]; note?: string } | { state: 'error'; message: string };

/** The details address for one option: the card's own signed link with /details/<index> added. */
const selectedUrl = (url: string) => {
  const u = new URL(url);
  u.pathname = `${u.pathname}/selected`;
  return u.toString();
};
const detailsUrl = (url: string, index: number) => {
  const u = new URL(url);
  u.pathname = `${u.pathname}/details/${index}`;
  return u.toString();
};

const CarrierChoiceCard: React.FC<{ url: string; onChoose?: (text: string) => void }> = ({ url, onChoose }) => {
  const [set, setSet] = useState<ChoiceSet | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [open, setOpen] = useState<Set<number>>(new Set());
  const [carrierDetails, setCarrierDetails] = useState<Record<number, CarrierDetails>>({});
  const [sent, setSent] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(url, { headers: HEADERS })
      .then(async (r) => {
        const body = await r.json().catch(() => null);
        if (!r.ok || !body?.options) throw new Error(body?.error ?? 'These options are no longer available — ask again.');
        if (!cancelled) setSet(body);
      })
      .catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [url]);

  if (error) return <div className="chat-choice-card__error">{error}</div>;
  if (!set) return <div className="chat-choice-card__loading">Loading options…</div>;

  const live = !!onChoose && sent === null;
  const multi = !!set.multi;

  const loadCarrierDetails = (i: number) => {
    setCarrierDetails((d) => ({ ...d, [i]: { state: 'loading' } }));
    fetch(detailsUrl(url, i), { headers: HEADERS })
      .then(async (r) => {
        const body = await r.json().catch(() => null);
        if (!r.ok || !body?.details) throw new Error(body?.error ?? `Couldn't read this policy from ${set.carrier ?? 'the carrier'}.`);
        setCarrierDetails((d) => ({ ...d, [i]: { state: 'done', details: body.details, note: body.note } }));
      })
      .catch((e) => setCarrierDetails((d) => ({ ...d, [i]: { state: 'error', message: e.message } })));
  };

  const toggleOpen = (i: number) => {
    setOpen((o) => {
      const next = new Set(o);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
    if (set.detailsAvailable && !carrierDetails[i]) loadCarrierDetails(i);
  };

  const send = (say: string) => { if (!live) return; setSent(say); onChoose!(say); };
  const toggleChecked = (i: number) => {
    if (!live) return;
    if (!multi) { send(set.options[i].say); return; }
    setChecked((c) => {
      const next = new Set(c);
      if (next.has(i)) next.delete(i); else next.add(i);
      // Tell the server what is ticked, so the agent can read it when the person types "the selected 2" instead of pressing the button.
      fetch(selectedUrl(url), { method: 'POST', headers: { ...HEADERS, 'Content-Type': 'application/json' }, body: JSON.stringify({ indexes: Array.from(next) }) }).catch(() => {});
      return next;
    });
  };
  const sendChecked = () => {
    const picked = Array.from(checked).sort((a, b) => a - b).map((i) => set.options[i]);
    if (!picked.length) return;
    send(picked.length === 1 ? picked[0].say : set.multi!.say.replace('{values}', picked.map((o) => o.value ?? o.label).join(', ')));
  };

  const columns = set.columns ?? ['Option'];
  const span = columns.length + 2;

  return (
    <div className="chat-choice-card">
      <table className="chat-choice-table">
        <thead>
          <tr>
            <th className="chat-choice-table__pick" aria-label="Select" />
            {columns.map((c) => <th key={c}>{c}</th>)}
            <th className="chat-choice-table__more" aria-label="Details" />
          </tr>
        </thead>
        <tbody>
          {set.options.map((o, i) => {
            const isChecked = checked.has(i) || (sent !== null && sent === o.say);
            const isOpen = open.has(i);
            const cd = carrierDetails[i];
            const cells = o.cells?.length ? o.cells : [o.label];
            return (
              <React.Fragment key={o.say}>
                <tr className={`chat-choice-row${isChecked ? ' chat-choice-row--picked' : ''}${live ? ' chat-choice-row--live' : ''}`}>
                  <td className="chat-choice-table__pick">
                    <button
                      type="button"
                      role={multi ? 'checkbox' : 'radio'}
                      aria-checked={isChecked}
                      aria-label={`Select ${o.label}`}
                      className={`chat-choice-bubble${isChecked ? ' chat-choice-bubble--on' : ''}`}
                      disabled={!live}
                      onClick={() => toggleChecked(i)}
                    >
                      {isChecked && <FiCheck aria-hidden />}
                    </button>
                  </td>
                  {cells.map((cell, j) => (
                    <td key={j} onClick={() => toggleChecked(i)}>{cell}</td>
                  ))}
                  <td className="chat-choice-table__more">
                    <button
                      type="button"
                      className={`chat-choice-more${isOpen ? ' chat-choice-more--open' : ''}`}
                      aria-expanded={isOpen}
                      aria-label={`${isOpen ? 'Hide' : 'Show'} details for ${o.label}`}
                      onClick={() => toggleOpen(i)}
                    >
                      <FiChevronDown aria-hidden />
                    </button>
                  </td>
                </tr>
                {isOpen && (
                  <tr className="chat-choice-details-row">
                    <td colSpan={span}>
                      <dl className="chat-choice-details">
                        {[...(o.details ?? []), ...(cd?.state === 'done' ? cd.details : [])].map((d, k) => (
                          <React.Fragment key={`${d.label}-${k}`}>
                            <dt>{d.label}</dt>
                            <dd>{d.value}</dd>
                          </React.Fragment>
                        ))}
                      </dl>
                      {cd?.state === 'loading' && (
                        <div className="chat-choice-card__loading">Reading this policy from {set.carrier ?? 'the carrier'}… this can take up to a minute.</div>
                      )}
                      {cd?.state === 'done' && cd.note && <div className="chat-choice-card__hint">{cd.note}</div>}
                      {cd?.state === 'error' && (
                        <div className="chat-choice-card__error">
                          {cd.message}{' '}
                          <button type="button" className="chat-choice-retry" onClick={() => loadCarrierDetails(i)}>Try again</button>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      {multi && (
        <div className="chat-choice-card__actions">
          <button
            type="button"
            className="chat-choice chat-choice--primary"
            disabled={!live || checked.size === 0}
            onClick={sendChecked}
          >
            {set.multi!.button}{checked.size ? ` (${checked.size})` : ''}
          </button>
          <span className="chat-choice-card__hint">
            {sent !== null ? 'Sent.' : checked.size ? `${checked.size} selected` : 'Tick one or more policies.'}
          </span>
        </div>
      )}
      {!onChoose && sent === null && <div className="chat-choice-card__hint">Selectable once the reply has finished.</div>}
    </div>
  );
};

export default CarrierChoiceCard;
